import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { useEditor, EditorContent } from '@tiptap/react';
import useApi from '../../hooks/useApi';
import AffiliateSidebarRail from '../../components/ads/AffiliateSidebarRail';
import NativeAffiliateBanner from '../../components/ads/NativeAffiliateBanner';
import AuthoredEditorMenuBar from '../../authored/AuthoredEditorMenuBar';
import {
  MAX_AUTHORED_BODY_WORDS,
  MAX_AUTHORED_HALF_PAGE_TEXT_WORDS,
  VISUAL_LAYOUT_FULL,
  VISUAL_LAYOUT_HALF,
  getAuthorPageTextPolicy,
  normalizeAuthorPageVisualUrls,
} from '../../authored/authorPageVisualPolicy';
import {
  AUTHORED_EDITOR_EXTENSION_SET_VERSION,
  countWordsFromDoc,
  getAuthoredEditorExtensions,
} from '../../authored/tiptapExtensions';
import {
  AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS,
  computeComposeEmbedGuards,
  computeEffectiveWordCeiling,
} from '../../authored/authorWordBudget';

const DEBOUNCE_MS = 900;

const BOOK_PAGE_MIN_H = 'min-h-[min(85vh,52rem)]';

const DEFAULT_BOOK_COVER_URL =
  'https://r2-file-server.mohamedzindha21.workers.dev/bookcovers/book_cover_plain.jpg';

/** Match AuthoredBookTheme reader: dir/lang from selected book language. */
function resolveAuthorLanguageAttrs(languageCode, languages, bookLanguage) {
  const fromList =
    languageCode && languages.length
      ? languages.find((l) => l.languages_code_unique_id === languageCode)
      : null;
  const langMeta = fromList || bookLanguage || null;
  return {
    textDir: langMeta?.text_direction === 'rtl' ? 'rtl' : 'ltr',
    langAttr:
      languageCode || langMeta?.languages_code_unique_id || undefined,
  };
}

function LanguageSelectListbox({
  languages,
  languageCode,
  setLanguageCode,
  onAfterChange,
  className = '',
}) {
  const selected = useMemo(() => {
    if (!languages.length) return null;
    return (
      languages.find((l) => l.languages_code_unique_id === languageCode) ?? languages[0]
    );
  }, [languages, languageCode]);

  if (!languages.length) {
    return (
      <div
        className={`w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 ${className}`}
      >
        Loading languages…
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Listbox
        value={selected}
        onChange={(lang) => {
          if (!lang) return;
          setLanguageCode(lang.languages_code_unique_id);
          onAfterChange?.();
        }}
      >
        <div className="relative mt-0.5">
          <Listbox.Button className="relative w-full cursor-default rounded-lg border border-slate-300 bg-white py-2.5 pl-3 pr-10 text-left text-sm text-slate-900 shadow-sm transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-800 focus:ring-offset-0">
            <span className="block truncate">
              {selected?.language_name || selected?.languages_code_unique_id || 'Language'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-slate-400" aria-hidden />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute z-40 mt-1 max-h-56 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200/80 focus:outline-none">
            {languages.map((l) => (
              <Listbox.Option
                key={l.languages_code_unique_id}
                value={l}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2.5 pl-3 pr-10 ${
                    active ? 'bg-violet-50 text-violet-950' : 'text-slate-800'
                  }`
                }
              >
                {({ selected: isSelected }) => (
                  <>
                    <span className="block truncate">
                      {l.language_name || l.languages_code_unique_id}
                    </span>
                    {isSelected ? (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-violet-900">
                        <CheckIcon className="h-5 w-5" aria-hidden />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

/** Shown on the write page after create — language is fixed at book creation. */
function BookLanguageReadOnly({ languages, languageCode, bookLanguage, className = '' }) {
  const label = useMemo(() => {
    const code =
      typeof languageCode === 'string'
        ? languageCode
        : languageCode?.languages_code_unique_id || '';
    const fromList = code && languages.length
      ? languages.find((l) => l.languages_code_unique_id === code)
      : null;
    const meta = fromList || bookLanguage;
    return meta?.language_name || meta?.languages_code_unique_id || code || '—';
  }, [languages, languageCode, bookLanguage]);

  return (
    <div
      className={`w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 ${className}`}
      aria-readonly="true"
    >
      {label}
    </div>
  );
}

export default function AuthorWritePage() {
  const { book_id: routeBookId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { request, loading, postMultipart } = useApi();
  const [languages, setLanguages] = useState([]);
  const [title, setTitle] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [bookDetail, setBookDetail] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [bookPagesLoading, setBookPagesLoading] = useState(
    () => routeBookId !== 'new' && Boolean(routeBookId),
  );
  const [saveState, setSaveState] = useState('idle');
  const [addingPage, setAddingPage] = useState(false);
  const [deletingPageId, setDeletingPageId] = useState(null);
  const [deletePageModal, setDeletePageModal] = useState(null); // { id, orderLabel } | null
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);
  const [visualLayoutChoice, setVisualLayoutChoice] = useState(VISUAL_LAYOUT_FULL);
  const [fullImageFile, setFullImageFile] = useState(null);
  const [halfImage0, setHalfImage0] = useState(null);
  const [halfImage1, setHalfImage1] = useState(null);
  /** `null` idle — distinguishes upload vs remove so the correct button shows a spinner */
  const [visualsOperation, setVisualsOperation] = useState(null); // null | 'upload' | 'remove'
  const visualsBusy = visualsOperation !== null;
  const [pageVisualsOpen, setPageVisualsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [pageWordCount, setPageWordCount] = useState(0);

  const isNew = routeBookId === 'new';
  const bookId = isNew ? null : routeBookId;
  /** Lets `setContent` load arbitrary server content while normal edits stay within the word cap. */
  const persistDraftRef = useRef({ tid: null, pending: null });
  const skipWordLimitRef = useRef(false);
  const skipWordLimit = useCallback(() => skipWordLimitRef.current, []);
  const editorGuardRef = useRef(null);
  /** Must not change identity per keystroke — `useEditor([extensions])` would remount and break typing. */
  const composeGuardInputRef = useRef({
    pageWordCount: 0,
    hasPageImages: false,
    applyWordCountGate: true,
  });

  const { textDir: authorTextDir, langAttr: authorLangAttr } = useMemo(
    () => resolveAuthorLanguageAttrs(languageCode, languages, bookDetail?.language),
    [languageCode, languages, bookDetail?.language],
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await request({
          method: 'get',
          url: '/api/v1/pdf_parser/languages/',
          withAuth: false,
        });
        setLanguages(data.languages || []);
        if (data.languages?.length && !languageCode) {
          setLanguageCode(data.languages[0].languages_code_unique_id);
        }
      } catch {
        setError('languages_failed');
      }
    })();
  }, [request]);

  const mergeFetchedPagesIntoState = useCallback((plist) => {
    setPages(plist);
    setActivePageId((prev) => {
      if (prev && plist.some((p) => p.authored_page_meta_unique_id === prev)) {
        return prev;
      }
      return plist[0]?.authored_page_meta_unique_id ?? null;
    });
  }, []);

  const mergeAuthorPageIntoList = useCallback((updated) => {
    const id = updated?.authored_page_meta_unique_id;
    if (!id) return;
    setPages((prev) =>
      prev.map((row) =>
        row.authored_page_meta_unique_id === id ? { ...row, ...updated } : row,
      ),
    );
  }, []);

  const loadBook = useCallback(async () => {
    if (!bookId) return;
    const data = await request({
      method: 'get',
      url: `/api/v1/pdf_parser/author/books/${bookId}/`,
      withAuth: true,
    });
    setBookDetail(data.book);
    setTitle(data.book.book_name || '');
    const lc = data.book.language_code;
    if (lc) {
      setLanguageCode(
        typeof lc === 'object' && lc !== null
          ? lc.languages_code_unique_id || ''
          : String(lc),
      );
    }
    const pdata = await request({
      method: 'get',
      url: `/api/v1/pdf_parser/author/books/${bookId}/pages/`,
      params: { start: 0, end: 199 },
      withAuth: true,
    });
    mergeFetchedPagesIntoState(pdata.pages || []);
  }, [bookId, request, mergeFetchedPagesIntoState]);

  useEffect(() => {
    if (!bookId) {
      setBookPagesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setBookPagesLoading(true);
    loadBook()
      .catch(() => {
        if (!cancelled) setError('book_load_failed');
      })
      .finally(() => {
        if (!cancelled) setBookPagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId, loadBook]);

  const activePage = useMemo(
    () => pages.find((p) => p.authored_page_meta_unique_id === activePageId),
    [pages, activePageId],
  );

  const activePageVisualUrls = useMemo(
    () => normalizeAuthorPageVisualUrls(activePage),
    [activePage],
  );

  const authorTextPolicy = useMemo(
    () => getAuthorPageTextPolicy(activePage, activePageVisualUrls),
    [activePage, activePageVisualUrls],
  );

  const getBaseMaxWords = useCallback(
    () => authorTextPolicy.maxWords,
    [authorTextPolicy.maxWords],
  );

  composeGuardInputRef.current = {
    pageWordCount,
    hasPageImages: activePageVisualUrls.length > 0,
    applyWordCountGate: authorTextPolicy.allowText,
  };

  const getComposeGuards = useCallback(() => {
    const ed = editorGuardRef.current;
    const input = composeGuardInputRef.current;
    return computeComposeEmbedGuards({
      pageWordCount: input.pageWordCount,
      hasPageImages: input.hasPageImages,
      doc: ed?.state?.doc,
      applyWordCountGate: input.applyWordCountGate,
    });
  }, []);

  const extensions = useMemo(
    () =>
      getAuthoredEditorExtensions({
        editable: true,
        getBaseMaxWords,
        shouldSkipWordLimit: skipWordLimit,
        getComposeGuards,
        textDir: authorTextDir,
      }),
    [
      skipWordLimit,
      getBaseMaxWords,
      getComposeGuards,
      authorTextDir,
      AUTHORED_EDITOR_EXTENSION_SET_VERSION,
    ],
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions,
      content: { type: 'doc', content: [] },
      editorProps: {
        attributes: {
          class:
            'tiptap-editor max-w-none min-h-[min(28rem,calc(100vh-22rem))] md:min-h-[32rem] px-3 py-3 border-0 rounded-none focus:outline-none text-slate-900 leading-relaxed authored-write-editor-surface',
          dir: authorTextDir,
          lang: authorLangAttr,
        },
      },
    },
    [extensions],
  );

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute('dir', authorTextDir);
    if (authorLangAttr) editor.view.dom.setAttribute('lang', authorLangAttr);
    else editor.view.dom.removeAttribute('lang');
  }, [editor, authorTextDir, authorLangAttr]);

  useEffect(() => {
    editorGuardRef.current = editor ?? null;
  }, [editor]);

  useEffect(() => {
    if (!activePage) return;
    const sz = activePage.visual_content_size;
    if (sz === VISUAL_LAYOUT_HALF || sz === VISUAL_LAYOUT_FULL) {
      setVisualLayoutChoice(sz);
    } else {
      setVisualLayoutChoice(VISUAL_LAYOUT_FULL);
    }
    setFullImageFile(null);
    setHalfImage0(null);
    setHalfImage1(null);
  }, [activePage?.authored_page_meta_unique_id]);

  const runPersistDraft = useCallback(
    async (pageId, bodyJson) => {
      if (!pageId || !bookId) return;
      setSaveState('saving');
      try {
        await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/author/pages/${pageId}/`,
          withAuth: true,
          data: { body_json: bodyJson },
        });
        setPages((prev) =>
          prev.map((row) =>
            row.authored_page_meta_unique_id === pageId ? { ...row, body_json: bodyJson } : row,
          ),
        );
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 1500);
      } catch {
        setSaveState('error');
      }
    },
    [bookId, request],
  );

  const enqueuePersistDraft = useCallback(
    (pageId, bodyJson) => {
      persistDraftRef.current.pending = { pageId, bodyJson };
      const prevTid = persistDraftRef.current.tid;
      if (prevTid) clearTimeout(prevTid);
      persistDraftRef.current.tid = setTimeout(() => {
        persistDraftRef.current.tid = null;
        const q = persistDraftRef.current.pending;
        if (q) void runPersistDraft(q.pageId, q.bodyJson);
      }, DEBOUNCE_MS);
    },
    [runPersistDraft],
  );

  /**
   * Forces any debounced body draft onto the server.
   * @param {{ blocking?: boolean }} [opts] — `blocking: false` merges pending edits into local `pages` and PATCHes in the background so the UI (e.g. switching pages) is not stalled on network latency. Navigation away still uses blocking flush so data is saved before unmount/route change.
   */
  const flushPersistDraft = useCallback(
    async (opts = {}) => {
      const blocking = opts.blocking !== false;
      const ref = persistDraftRef.current;
      if (ref.tid) {
        clearTimeout(ref.tid);
        ref.tid = null;
      }
      const q = ref.pending;
      ref.pending = null;
      if (!q?.pageId || q.bodyJson === undefined || q.bodyJson === null) return;

      setPages((prev) =>
        prev.map((row) =>
          row.authored_page_meta_unique_id === q.pageId
            ? { ...row, body_json: q.bodyJson }
            : row,
        ),
      );

      if (blocking) {
        await runPersistDraft(q.pageId, q.bodyJson);
      } else {
        void runPersistDraft(q.pageId, q.bodyJson);
      }
    },
    [runPersistDraft],
  );

  const leaveTo = useCallback(
    (path) => {
      void (async () => {
        await flushPersistDraft();
        navigate(path);
      })();
    },
    [flushPersistDraft, navigate],
  );

  const togglePageVisualsFromToolbar = useCallback(() => {
    void flushPersistDraft({ blocking: false });
    setPageVisualsOpen((open) => !open);
  }, [flushPersistDraft]);

  /** Open last-written page from `?page=` (1-based `page_order_no`, matches `poppyruz_pages_count`). */
  useEffect(() => {
    if (!pages.length) return;
    const raw = searchParams.get('page');
    if (!raw) return;
    const order = parseInt(raw, 10);
    if (Number.isNaN(order) || order < 1) return;
    const byOrder = pages.find((p) => Number(p.page_order_no) === order);
    const fallback = [...pages].sort(
      (a, b) => (Number(b.page_order_no) || 0) - (Number(a.page_order_no) || 0),
    )[0];
    const target = byOrder || fallback;
    if (target) {
      void (async () => {
        await flushPersistDraft();
        setActivePageId(target.authored_page_meta_unique_id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('page');
            return next;
          },
          { replace: true },
        );
      })();
    }
  }, [pages, searchParams, setSearchParams, flushPersistDraft]);

  useEffect(() => {
    if (!editor || !activePageId) return;
    const json = activePage?.body_json || { type: 'doc', content: [] };
    skipWordLimitRef.current = true;
    editor.commands.setContent(json, false);
    skipWordLimitRef.current = false;
    queueMicrotask(() => {
      setPageWordCount(countWordsFromDoc(editor.state.doc));
    });
  }, [editor, activePageId, activePage?.authored_page_meta_unique_id]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(Boolean(authorTextPolicy.allowText));
  }, [editor, authorTextPolicy.allowText]);

  useEffect(() => {
    if (!editor) return;
    const syncWords = () => setPageWordCount(countWordsFromDoc(editor.state.doc));
    editor.on('transaction', syncWords);
    return () => editor.off('transaction', syncWords);
  }, [editor]);

  useEffect(() => {
    if (!editor || !activePageId || !authorTextPolicy.allowText) return;
    const onUpdate = () => {
      enqueuePersistDraft(activePageId, editor.getJSON());
    };
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor, activePageId, authorTextPolicy.allowText, enqueuePersistDraft]);


  const createBook = async () => {
    if (!title.trim()) {
      setError('title_required');
      return;
    }
    if (!languageCode) {
      setError('language_required');
      return;
    }
    setError(null);
    const data = await request({
      method: 'post',
      url: '/api/v1/pdf_parser/author/books/',
      withAuth: true,
      data: {
        book_name: title.trim().slice(0, 50),
        language_code: languageCode,
      },
    });
    const id = data.book?.book_meta_unique_id;
    if (!id) throw new Error('no book id');
    await request({
      method: 'post',
      url: `/api/v1/pdf_parser/author/books/${id}/pages/`,
      withAuth: true,
      data: {},
    });
    navigate(`/author/write/${id}`, { replace: true });
  };

  const addPage = async () => {
    if (!bookId || addingPage || bookPagesLoading) return;
    setAddingPage(true);
    try {
      await flushPersistDraft();
      const data = await request({
        method: 'post',
        url: `/api/v1/pdf_parser/author/books/${bookId}/pages/`,
        withAuth: true,
        data: {},
      });
      const p = data.page;
      setPages((prev) => [...prev, p]);
      setActivePageId(p.authored_page_meta_unique_id);
    } finally {
      setAddingPage(false);
    }
  };

  const openDeleteAuthorPageModal = useCallback((pageIdToDelete, pageOrderLabel) => {
    if (pages.length <= 1 || deletingPageId !== null) return;
    setPageVisualsOpen(false);
    setDeletePageModal({ id: pageIdToDelete, orderLabel: pageOrderLabel });
  }, [pages.length, deletingPageId]);

  const executeDeleteAuthorPage = async (pageIdToDelete, pageOrderLabel) => {
    if (!bookId || pages.length <= 1 || deletingPageId) return;
    setError(null);

    if (pageIdToDelete === activePageId) {
      const ref = persistDraftRef.current;
      if (ref.tid) clearTimeout(ref.tid);
      ref.tid = null;
      ref.pending = null;
    } else {
      await flushPersistDraft();
    }

    setDeletingPageId(pageIdToDelete);
    try {
      await request({
        method: 'delete',
        url: `/api/v1/pdf_parser/author/pages/${pageIdToDelete}/`,
        withAuth: true,
      });
      const pdata = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/author/books/${bookId}/pages/`,
        params: { start: 0, end: 199 },
        withAuth: true,
      });
      mergeFetchedPagesIntoState(pdata.pages || []);
      setPageVisualsOpen(false);
    } catch {
      setError('delete_page_failed');
    } finally {
      setDeletingPageId(null);
    }
  };

  const closeDeleteAuthorPageModal = () => setDeletePageModal(null);

  const submitPageVisuals = async () => {
    if (!activePageId || visualsBusy || !editor) return;
    setError(null);

    const embedGuards = computeComposeEmbedGuards({
      pageWordCount,
      hasPageImages: activePageVisualUrls.length > 0,
      doc: editor.state.doc,
      applyWordCountGate: authorTextPolicy.allowText,
    });
    if (embedGuards.blockPageVisuals) {
      setError('visuals_embed_conflict');
      return;
    }
    if (embedGuards.blockFullPageVisuals && visualLayoutChoice === VISUAL_LAYOUT_FULL) {
      setError('visuals_full_needs_fewer_words');
      return;
    }
    const curLayout = activePage?.visual_content_size;
    const hasViz = activePageVisualUrls.length > 0;
    if (hasViz && curLayout === VISUAL_LAYOUT_HALF && visualLayoutChoice === VISUAL_LAYOUT_FULL) {
      setError('visuals_half_blocks_full');
      return;
    }
    if (hasViz && curLayout === VISUAL_LAYOUT_FULL && visualLayoutChoice === VISUAL_LAYOUT_HALF) {
      setError('visuals_full_blocks_half');
      return;
    }

    if (visualLayoutChoice === VISUAL_LAYOUT_FULL) {
      if (!fullImageFile) {
        setError('visuals_pick_file');
        return;
      }
    } else if (!halfImage0) {
      setError('visuals_pick_half_first');
      return;
    }

    const fd = new FormData();
    fd.append('layout', visualLayoutChoice);
    if (visualLayoutChoice === VISUAL_LAYOUT_FULL) {
      fd.append('image', fullImageFile);
    } else {
      fd.append('image_0', halfImage0);
      if (halfImage1) {
        fd.append('image_1', halfImage1);
      }
    }

    setVisualsOperation('upload');
    try {
      const data = await postMultipart({
        url: `/api/v1/pdf_parser/author/pages/${activePageId}/visuals/`,
        formData: fd,
      });
      if (data?.page) mergeAuthorPageIntoList(data.page);
      setFullImageFile(null);
      setHalfImage0(null);
      setHalfImage1(null);
    } catch {
      setError('visuals_upload_failed');
    } finally {
      setVisualsOperation(null);
    }
  };

  const clearPageVisuals = async () => {
    if (!activePageId || !activePage || visualsBusy) return;
    const hasVis =
      normalizeAuthorPageVisualUrls(activePage).length > 0 ||
      Number(activePage.has_visual_content) === 1;
    if (!hasVis) return;

    setVisualsOperation('remove');
    setError(null);
    try {
      const data = await request({
        method: 'delete',
        url: `/api/v1/pdf_parser/author/pages/${activePageId}/visuals/`,
        withAuth: true,
      });
      if (data?.page) mergeAuthorPageIntoList(data.page);
    } catch {
      setError('visuals_clear_failed');
    } finally {
      setVisualsOperation(null);
    }
  };

  const uploadBookCover = async (file) => {
    if (!bookId || !file || coverUploading) return;
    setCoverUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const data = await postMultipart({
        url: `/api/v1/pdf_parser/author/books/${bookId}/cover/`,
        formData: fd,
      });
      if (data?.book) {
        setBookDetail(data.book);
      }
    } catch {
      setError('cover_upload_failed');
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  const onBookCoverFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      void uploadBookCover(file);
    }
  };

  const saveMeta = async () => {
    if (!bookId) return;
    await request({
      method: 'patch',
      url: `/api/v1/pdf_parser/author/books/${bookId}/`,
      withAuth: true,
      data: {
        book_name: title.trim().slice(0, 50),
      },
    });
    setSaveState('saved');
  };

  const executePublish = async () => {
    if (!bookId || publishing) return;
    setPublishing(true);
    setError(null);
    try {
      await request({
        method: 'patch',
        url: `/api/v1/pdf_parser/author/books/${bookId}/`,
        withAuth: true,
        data: { publication_status: 'PB' },
      });
      setBookDetail((b) => ({ ...b, publication_status: 'PB' }));
      setShowPublishModal(false);
    } catch {
      setError('publish_failed');
    } finally {
      setPublishing(false);
    }
  };

  const effectiveWordCeiling =
    editor && authorTextPolicy.allowText && authorTextPolicy.maxWords > 0
      ? computeEffectiveWordCeiling(authorTextPolicy.maxWords, editor.state.doc)
      : authorTextPolicy.maxWords;

  const pageVisualEmbedGuards = computeComposeEmbedGuards({
    pageWordCount,
    hasPageImages: activePageVisualUrls.length > 0,
    doc: editor?.state?.doc,
    applyWordCountGate: authorTextPolicy.allowText,
  });
  const disableFullPageVisualRadio =
    pageVisualEmbedGuards.blockFullPageVisuals ||
    Boolean(
      activePageVisualUrls.length &&
        activePage?.visual_content_size === VISUAL_LAYOUT_HALF,
    );
  const disableHalfPageVisualRadio =
    Boolean(
      activePageVisualUrls.length &&
        activePage?.visual_content_size === VISUAL_LAYOUT_FULL,
    );
  const visualsPanelDisabled =
    pageVisualEmbedGuards.blockPageVisuals ||
    !authorTextPolicy.allowText ||
    authorTextPolicy.maxWords <= 0;

  const authoredMenubarEmbed = {
    baseMaxWords: authorTextPolicy.maxWords,
    pageWordCount,
    hasPageImages: activePageVisualUrls.length > 0,
    applyWordCountGate: authorTextPolicy.allowText,
  };

  if (isNew) {
    return (
      <div className="relative flex min-h-screen flex-col justify-center bg-slate-50 px-4 py-16">
        <Link
          to="/author"
          aria-label="Back to author home"
          onClick={(e) => {
            e.preventDefault();
            leaveTo('/author');
          }}
          className="fixed top-4 left-4 z-20 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-md transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-950 md:top-6 md:left-6"
        >
          <ArrowLeftIcon className="h-8 w-8" aria-hidden />
        </Link>
        <div className="mx-auto flex w-full max-w-5xl items-center justify-center gap-16 xl:gap-24">
          <AffiliateSidebarRail
            placement="author_new_left"
            salt={7}
            productCount={4}
            size="compact"
            layout="grid"
          />
          <div
            className="w-full max-w-md shrink-0 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            lang={authorLangAttr}
            dir={authorTextDir}
          >
            <h1 className="text-xl font-semibold mb-6">New book</h1>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2 mb-4"
              dir={authorTextDir}
              lang={authorLangAttr}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
            />
            <label className="block text-sm font-medium text-slate-700 mb-1">Language</label>
            <LanguageSelectListbox
              languages={languages}
              languageCode={languageCode}
              setLanguageCode={setLanguageCode}
              className="mb-6"
            />
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            <button
              type="button"
              disabled={loading}
              onClick={() => createBook().catch(() => setError('create_failed'))}
              className="w-full py-2 bg-violet-900 text-white rounded-lg font-medium disabled:opacity-50"
            >
              Create & start writing
            </button>
          </div>
          <AffiliateSidebarRail
            placement="author_new_right"
            salt={11}
            productCount={4}
            size="compact"
            layout="grid"
          />
        </div>
        <div className="mx-auto mt-10 w-full max-w-5xl lg:hidden">
          <NativeAffiliateBanner placement="author_new_mobile" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-50"
      lang={authorLangAttr}
      dir={authorTextDir}
    >
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between gap-3">
        <Link
          to="/author"
          onClick={(e) => {
            e.preventDefault();
            leaveTo('/author');
          }}
          className="inline-flex shrink-0 items-center self-center text-sm leading-none text-violet-900 hover:underline py-1.5"
        >
          ← Author home
        </Link>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 text-sm sm:gap-3">
          {(saveState === 'saving' || saveState === 'saved' || saveState === 'error') && (
            <span className="text-slate-500 whitespace-nowrap">
              {saveState === 'saving' && 'Saving…'}
              {saveState === 'saved' && 'Saved'}
              {saveState === 'error' && 'Save failed'}
            </span>
          )}
          {bookDetail?.publication_status === 'PB' ? (
            <>
              <Link
                to={`/read_authored/${bookId}`}
                onClick={(e) => {
                  e.preventDefault();
                  leaveTo(`/read_authored/${bookId}`);
                }}
                className="text-violet-900 font-medium hover:underline"
              >
                Open reader
              </Link>
              <button
                type="button"
                onClick={() =>
                  window.open(`/read_authored/${bookId}?preview=1`, '_blank', 'noopener,noreferrer')
                }
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"
              >
                Preview
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() =>
                  bookId &&
                  window.open(`/read_authored/${bookId}?preview=1`, '_blank', 'noopener,noreferrer')
                }
                disabled={!bookId}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={() => setShowPublishModal(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
              >
                Publish
              </button>
            </>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex items-start justify-center gap-6 xl:gap-8">
          <AffiliateSidebarRail placement="author_write_left" salt={2} productCount={2} />

          <div className="w-full max-w-4xl shrink-0 space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => saveMeta().catch(() => {})}
              maxLength={50}
              dir={authorTextDir}
              lang={authorLangAttr}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Language</label>
            <BookLanguageReadOnly
              languages={languages}
              languageCode={languageCode}
              bookLanguage={bookDetail?.language}
            />
            <p className="mt-1 text-xs text-slate-500">Set when you create the book.</p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">Book cover</label>
          <div className="flex flex-wrap items-start gap-4">
            <img
              src={bookDetail?.book_cover || DEFAULT_BOOK_COVER_URL}
              alt=""
              className="h-32 w-24 shrink-0 rounded-md border border-slate-200 object-cover shadow-sm"
            />
            <div className="flex min-w-[12rem] flex-col gap-2">
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={onBookCoverFileChange}
              />
              <button
                type="button"
                disabled={coverUploading}
                onClick={() => coverInputRef.current?.click()}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {coverUploading ? 'Uploading…' : 'Upload cover'}
              </button>
              <p className="text-xs text-slate-500">JPEG, PNG, or WebP. Max 10MB.</p>
            </div>
          </div>
        </div>

        {error === 'cover_upload_failed' && (
          <p className="text-sm text-red-600">
            Could not upload cover. Use JPEG, PNG, or WebP under 10MB and try again.
          </p>
        )}
        {error === 'delete_page_failed' && (
          <p className="text-sm text-red-600">Could not delete this page. Try again.</p>
        )}
        {error === 'visuals_upload_failed' && (
          <p className="text-sm text-red-600">
            Could not upload images. Use JPEG, PNG, or WebP under 10MB and try again.
          </p>
        )}
        {error === 'visuals_clear_failed' && (
          <p className="text-sm text-red-600">Could not remove images. Try again.</p>
        )}
        {error === 'visuals_pick_file' && (
          <p className="text-sm text-red-600">Choose one image for full-page layout.</p>
        )}
        {error === 'visuals_pick_half_first' && (
          <p className="text-sm text-red-600">Choose at least the first image for half-page layout.</p>
        )}
        {error === 'visuals_embed_conflict' && (
          <p className="text-sm text-red-600">
            Remove tables and code blocks (and keep body text at {AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS} words or below) before
            adding page images.
          </p>
        )}
        {error === 'visuals_full_needs_fewer_words' && (
          <p className="text-sm text-red-600">
            Full-page images are only allowed when the editor has no words. After you add text, use half-page images
            (not available once the page goes over {AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS} words).
          </p>
        )}
        {error === 'visuals_half_blocks_full' && (
          <p className="text-sm text-red-600">This page uses half-page images. Clear them before switching to full-page.</p>
        )}
        {error === 'visuals_full_blocks_half' && (
          <p className="text-sm text-red-600">This page uses a full-bleed image. Clear it before switching to half-page.</p>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-600">Pages:</span>
          {pages.map((p) => {
            const isActive = p.authored_page_meta_unique_id === activePageId;
            const orderLabel = p.page_order_no ?? '?';
            const busy = deletingPageId === p.authored_page_meta_unique_id;
            return (
              <span
                key={p.authored_page_meta_unique_id}
                className="inline-flex items-stretch rounded border border-slate-200 overflow-hidden bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => {
                    void flushPersistDraft({ blocking: false });
                    setActivePageId(p.authored_page_meta_unique_id);
                    setPageVisualsOpen(false);
                  }}
                  className={`text-sm px-2 py-1 border-0 transition ${
                    isActive
                      ? 'bg-violet-50 text-violet-950 ring-1 ring-inset ring-violet-400'
                      : 'bg-white text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  {orderLabel}
                </button>
                <button
                  type="button"
                  disabled={pages.length <= 1 || deletingPageId !== null}
                  aria-label={`Delete page ${orderLabel}`}
                  title={
                    pages.length <= 1
                      ? 'A book needs at least one page'
                      : `Delete page ${orderLabel}`
                  }
                  onClick={() =>
                    openDeleteAuthorPageModal(p.authored_page_meta_unique_id, String(orderLabel))
                  }
                  className="flex items-center justify-center px-1.5 border-l border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  {busy ? (
                    <ArrowPathIcon
                      className="h-4 w-4 shrink-0 animate-spin text-slate-500"
                      aria-hidden
                    />
                  ) : (
                    <TrashIcon className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                </button>
              </span>
            );
          })}
          <button
            type="button"
            disabled={addingPage || bookPagesLoading}
            aria-busy={addingPage || bookPagesLoading}
            onClick={() => addPage().catch(() => setError('add_page_failed'))}
            className="inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded border border-dashed border-slate-400 text-slate-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {addingPage ? (
              <>
                <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin text-slate-500" aria-hidden />
                Adding…
              </>
            ) : bookPagesLoading ? (
              <>
                <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin text-slate-500" aria-hidden />
                Loading…
              </>
            ) : (
              '+ Page'
            )}
          </button>
        </div>

        {activePage && pageVisualsOpen && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium text-slate-800 mb-1">Page visuals</h3>
                <p className="text-xs text-slate-500">
                  Optional images stored on our CDN. Full page: one wide image (max 1400×2000 after upload). Half page:
                  fits up to two smaller slots stacked vertically in the reader (700×1000 each); you can upload one or two
                  images. JPEG, PNG, or WebP — up to 2MB per file.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPageVisualsOpen(false)}
                className="shrink-0 text-sm text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
              >
                Close
              </button>
            </div>

            <fieldset disabled={visualsPanelDisabled || visualsBusy} className="space-y-2">
              <legend className="text-xs font-medium text-slate-600 mb-2">Layout for next upload</legend>
              <label
                className={`flex items-center gap-2 text-sm ${
                  disableFullPageVisualRadio ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name="visualLayout"
                  disabled={disableFullPageVisualRadio}
                  checked={visualLayoutChoice === VISUAL_LAYOUT_FULL}
                  onChange={() => {
                    setVisualLayoutChoice(VISUAL_LAYOUT_FULL);
                    setError(null);
                  }}
                  className="text-violet-900 disabled:opacity-40"
                />
                Full page (one image){' '}
                {pageVisualEmbedGuards.blockFullPageVisuals ? (
                  <span className="text-[11px] text-slate-400">
                    {' '}
                    — only when there are 0 words (half-page if the page has text and ≤{AUTHOR_MAX_WORDS_FOR_RICH_EMBEDS}{' '}
                    words)
                  </span>
                ) : null}
              </label>
              <label
                className={`flex items-center gap-2 text-sm ${
                  disableHalfPageVisualRadio ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name="visualLayout"
                  disabled={disableHalfPageVisualRadio}
                  checked={visualLayoutChoice === VISUAL_LAYOUT_HALF}
                  onChange={() => {
                    setVisualLayoutChoice(VISUAL_LAYOUT_HALF);
                    setError(null);
                  }}
                  className="text-violet-900 disabled:opacity-40"
                />
                Half page (one or two images, stacked vertically)
              </label>
            </fieldset>

            <div key={activePage.authored_page_meta_unique_id} className="space-y-2">
              {visualLayoutChoice === VISUAL_LAYOUT_FULL ? (
                <label className="block text-xs font-medium text-slate-600">
                  Image file
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={visualsBusy || visualsPanelDisabled}
                    className="mt-1 block w-full text-sm text-slate-600"
                    onChange={(e) => {
                      setFullImageFile(e.target.files?.[0] || null);
                      setError(null);
                    }}
                  />
                </label>
              ) : (
                <div className="flex flex-col gap-3">
                  <label className="block text-xs font-medium text-slate-600">
                    Image 1 (top)
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={visualsBusy || visualsPanelDisabled}
                      className="mt-1 block w-full text-sm text-slate-600"
                      onChange={(e) => {
                        setHalfImage0(e.target.files?.[0] || null);
                        setError(null);
                      }}
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-600">
                    Image 2 (below, optional)
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={visualsBusy || visualsPanelDisabled}
                      className="mt-1 block w-full text-sm text-slate-600"
                      onChange={(e) => {
                        setHalfImage1(e.target.files?.[0] || null);
                        setError(null);
                      }}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={visualsBusy || visualsPanelDisabled}
                aria-busy={visualsOperation === 'upload'}
                onClick={() => submitPageVisuals().catch(() => {})}
                className="inline-flex items-center gap-1.5 rounded-md bg-violet-900 px-3 py-2 text-sm text-white hover:bg-violet-950 disabled:opacity-50"
              >
                {visualsOperation === 'upload' ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                    Uploading…
                  </>
                ) : (
                  'Upload images'
                )}
              </button>
              <button
                type="button"
                disabled={
                  visualsBusy ||
                  (activePageVisualUrls.length === 0 &&
                    Number(activePage.has_visual_content) !== 1)
                }
                aria-busy={visualsOperation === 'remove'}
                onClick={() => clearPageVisuals().catch(() => {})}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                {visualsOperation === 'remove' ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin text-slate-500" aria-hidden />
                    Removing…
                  </>
                ) : (
                  'Remove images'
                )}
              </button>
            </div>

            {activePageVisualUrls.length ? (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Current ({activePage.visual_content_size})</p>
                <div className="flex flex-wrap gap-2">
                  {activePageVisualUrls.map((u) => (
                    <img
                      key={u}
                      src={u}
                      alt=""
                      className="max-h-28 rounded border border-slate-200 object-contain bg-slate-50"
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {activePage && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-xs text-slate-500">
                {authorTextPolicy.layoutMode === 'full_bleed' ||
                authorTextPolicy.layoutMode === 'half_two_images' ? (
                  <>
                    Full-bleed image page — text is disabled. Use the photo button in the bar above (or toolbar)
                    to change or remove visuals if you want a text page.
                  </>
                ) : authorTextPolicy.layoutMode === 'half_one_image' ? (
                  <>
                    Half-page image · prose in the lower half only · max {MAX_AUTHORED_HALF_PAGE_TEXT_WORDS}{' '}
                    words · autosaves while you type.
                  </>
                ) : (
                  <>
                    Rich text (lists, tables, headings). Autosaves while you type. Max {MAX_AUTHORED_BODY_WORDS}{' '}
                    words per page.
                  </>
                )}
              </p>
              {authorTextPolicy.allowText ? (
                <p
                  className={`text-xs tabular-nums ${
                    pageWordCount >= effectiveWordCeiling
                      ? 'text-amber-700 font-medium'
                      : 'text-slate-500'
                  }`}
                  aria-live="polite"
                  title={
                    effectiveWordCeiling !== authorTextPolicy.maxWords
                      ? `Layout max ${authorTextPolicy.maxWords}; headings, list items, table rows, and code reduce how much prose fits.`
                      : undefined
                  }
                >
                  {pageWordCount}/{effectiveWordCeiling}
                  {effectiveWordCeiling !== authorTextPolicy.maxWords ? (
                    <span className="text-slate-400"> (of {authorTextPolicy.maxWords})</span>
                  ) : null}{' '}
                  words
                </p>
              ) : null}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              {authorTextPolicy.layoutMode === 'full_bleed' ? (
                <>
                  <AuthoredEditorMenuBar
                    editor={editor}
                    variant="visualsOnly"
                    embedToolbar={authoredMenubarEmbed}
                    onTogglePageVisuals={togglePageVisualsFromToolbar}
                    pageVisualsOpen={pageVisualsOpen}
                    textDir={authorTextDir}
                  />
                  <div className={`relative w-full shrink-0 bg-slate-50/90 ${BOOK_PAGE_MIN_H}`}>
                    <div className="absolute inset-4 overflow-hidden rounded-xl border border-slate-300 bg-stone-200 shadow-sm md:inset-6">
                      <img
                        src={activePageVisualUrls[0]}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                </>
              ) : authorTextPolicy.layoutMode === 'half_two_images' ? (
                <>
                  <AuthoredEditorMenuBar
                    editor={editor}
                    variant="visualsOnly"
                    embedToolbar={authoredMenubarEmbed}
                    onTogglePageVisuals={togglePageVisualsFromToolbar}
                    pageVisualsOpen={pageVisualsOpen}
                    textDir={authorTextDir}
                  />
                  <div className={`box-border flex w-full flex-col gap-4 p-4 md:gap-5 md:p-6 ${BOOK_PAGE_MIN_H}`}>
                    {[0, 1].map((slot) => (
                      <div
                        key={activePageVisualUrls[slot] ?? slot}
                        className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-300 bg-stone-200 shadow-sm"
                      >
                        {activePageVisualUrls[slot] ? (
                          <img
                            src={activePageVisualUrls[slot]}
                            alt=""
                            className="absolute inset-0 h-full w-full object-contain"
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              ) : authorTextPolicy.layoutMode === 'half_one_image' ? (
                <div className={`flex w-full flex-col ${BOOK_PAGE_MIN_H}`}>
                  <div className="relative flex min-h-0 shrink-0 basis-1/2 min-h-[12rem] items-stretch justify-center border-b border-slate-200 bg-slate-50/90 px-5 py-5 md:px-7 md:py-7">
                    {activePageVisualUrls[0] ? (
                      <div className="relative flex w-full min-h-[11rem] min-w-0 flex-1 overflow-hidden rounded-xl border border-slate-300 bg-stone-200 shadow-sm md:min-h-[13rem]">
                        <img
                          src={activePageVisualUrls[0]}
                          alt=""
                          className="absolute inset-0 h-full w-full object-contain"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col [&_.tiptap-editor]:min-h-[min(18rem,42vh)]">
                    <AuthoredEditorMenuBar
                      editor={editor}
                      embedToolbar={authoredMenubarEmbed}
                      onTogglePageVisuals={togglePageVisualsFromToolbar}
                      pageVisualsOpen={pageVisualsOpen}
                      textDir={authorTextDir}
                    />
                    <EditorContent editor={editor} />
                  </div>
                </div>
              ) : (
                <>
                  <AuthoredEditorMenuBar
                    editor={editor}
                    embedToolbar={authoredMenubarEmbed}
                    onTogglePageVisuals={togglePageVisualsFromToolbar}
                    pageVisualsOpen={pageVisualsOpen}
                    textDir={authorTextDir}
                  />
                  <EditorContent editor={editor} />
                </>
              )}
            </div>
          </div>
        )}
          </div>

          <AffiliateSidebarRail placement="author_write_right" salt={6} productCount={2} />
        </div>

        <div className="mt-8 lg:hidden">
          <NativeAffiliateBanner placement="author_write_mobile" />
        </div>
      </div>

      {showPublishModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-book-title"
            className="w-[300px] space-y-4 rounded-lg bg-white p-6 text-center shadow-lg"
          >
            <h2 id="publish-book-title" className="text-lg font-semibold text-slate-900">
              Publish Book?
            </h2>
            <p className="text-sm text-slate-600">
              Readers will see it at the public link.
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => void executePublish()}
                disabled={publishing}
                className="rounded bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {publishing ? 'Publishing…' : 'Yes, Publish'}
              </button>
              <button
                type="button"
                onClick={() => setShowPublishModal(false)}
                disabled={publishing}
                className="rounded bg-gray-300 px-4 py-2 hover:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deletePageModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-author-page-title"
            className="w-[300px] space-y-4 rounded-lg bg-white p-6 text-center shadow-lg"
          >
            <h2 id="delete-author-page-title" className="text-lg font-semibold text-slate-900">
              Delete Page?
            </h2>
            <p className="text-sm text-slate-600">
              Are you sure you want to delete page {deletePageModal.orderLabel}? This cannot be undone.
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  const { id, orderLabel } = deletePageModal;
                  closeDeleteAuthorPageModal();
                  void executeDeleteAuthorPage(id, orderLabel);
                }}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                type="button"
                onClick={closeDeleteAuthorPageModal}
                className="rounded bg-gray-300 px-4 py-2 hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
